// utils/API/BackendHelper/student/exam.js
import ApiHelper from '../../../ApiHelper';
import API_ENDPOINTS from '../../../endpoints';

class StudentExamHelper {
  /**
   * Get all available exams for the student
   * @returns {Promise} List of available exams with their status
   */
  async getAvailableExams() {
    try {
      const response = await ApiHelper.get(API_ENDPOINTS.STUDENTS.EXAMS.AVAILABLE);
      return response;
    } catch (error) {
      throw new Error(`Failed to fetch available exams: ${error.message}`);
    }
  }

  /**
   * Start an exam
   * @param {string} examId - The exam ID to start
   * @returns {Promise} Exam attempt details with questions
   */
  async startExam(examId) {
    try {
      const response = await ApiHelper.post(API_ENDPOINTS.STUDENTS.EXAMS.START(examId));
      return response;
    } catch (error) {
      throw new Error(`Failed to start exam: ${error.message}`);
    }
  }

  /**
   * Save/update an answer for a question (auto-save)
   * @param {string} attemptId - The attempt ID
   * @param {string} questionId - The question ID
   * @param {string} selectedAnswer - The selected answer option
   * @returns {Promise} Saved answer data
   */
  async saveAnswer(attemptId, questionId, selectedAnswer) {
    try {
      const response = await ApiHelper.post(
        API_ENDPOINTS.STUDENTS.EXAMS.SAVE_ANSWER(attemptId),
        { questionId, selectedAnswer }
      );
      return response;
    } catch (error) {
      throw new Error(`Failed to save answer: ${error.message}`);
    }
  }

  /**
   * Submit the exam
   * @param {string} attemptId - The attempt ID to submit
   * @returns {Promise} Submission result with score
   */
  async submitExam(attemptId) {
    try {
      const response = await ApiHelper.post(API_ENDPOINTS.STUDENTS.EXAMS.SUBMIT(attemptId));
      return response;
    } catch (error) {
      throw new Error(`Failed to submit exam: ${error.message}`);
    }
  }

  /**
   * Get exam results
   * @param {string} attemptId - The attempt ID
   * @returns {Promise} Detailed exam results with answers
   */
  async getExamResults(attemptId) {
    try {
      const response = await ApiHelper.get(API_ENDPOINTS.STUDENTS.EXAMS.RESULTS(attemptId));
      return response;
    } catch (error) {
      throw new Error(`Failed to fetch exam results: ${error.message}`);
    }
  }

  /**
   * Check if exam results have been declared
   * @param {string} examId - The exam ID
   * @returns {Promise} Results declaration status
   */
  async checkResultsStatus(examId) {
    try {
      const response = await ApiHelper.get(API_ENDPOINTS.STUDENTS.EXAMS.RESULTS_STATUS(examId));
      return response;
    } catch (error) {
      throw new Error(`Failed to check results status: ${error.message}`);
    }
  }

  /**
   * Get exam history for the student
   * @returns {Promise} List of all exam attempts
   */
  async getExamHistory() {
    try {
      const response = await ApiHelper.get(API_ENDPOINTS.STUDENTS.EXAMS.HISTORY);
      return response;
    } catch (error) {
      throw new Error(`Failed to fetch exam history: ${error.message}`);
    }
  }
}

export default new StudentExamHelper();